/**
 * @fileoverview Task files section for the key-date side panel.
 *
 * Renders the job-library documents linked to a task, with controls to add
 * (upload or link an existing file, via `TaskAttachmentsDialog`) and remove.
 * Clicking a file opens the shared `DocumentDetail` slide-over.
 *
 * Attachments require a persisted task id, so the caller only renders this
 * for materialised rows (matching the dependencies / notes sections, which
 * are also persisted-task only).
 *
 * Dependencies:
 * - api.tasks.attachments.listTaskAttachments / detachTaskDocument
 * - TaskAttachmentsDialog (add flow)
 * - DocumentDetail (viewer)
 */

import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import { toast } from "sonner";
import { FileText, Plus, X } from "lucide-react";

import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";

import { Button } from "@/ui/primitives/button";
import { extractErrorMessage } from "@/utils/errors";
import { DocumentDetail } from "@/components/jobs/phases/gather/documents/DocumentDetail";
import { TaskAttachmentsDialog } from "@/calendar/task-attachments-dialog";

export function TaskAttachmentsSection({
  keyDateId,
  jobId,
}: {
  keyDateId: Id<"jobKeyDates">;
  jobId: Id<"jobs">;
}) {
  const [addOpen, setAddOpen] = useState(false);
  const [viewingId, setViewingId] = useState<Id<"documents"> | null>(null);

  const detach = useMutation({
    mutationFn: useConvexMutation(api.tasks.attachments.detachTaskDocument),
  });

  const { data: attachments } = useQuery(
    convexQuery(api.tasks.attachments.listTaskAttachments, { keyDateId }),
  );

  const files = attachments ?? [];

  async function handleDetach(documentId: Id<"documents">) {
    try {
      await detach.mutateAsync({ keyDateId, documentId });
    } catch (error) {
      toast.error(extractErrorMessage(error, "Failed to remove the file"));
    }
  }

  return (
    <div>
      <div className="flex items-center justify-between">
        <h4 className="font-sans text-xs font-semibold uppercase tracking-wider text-foreground">
          Files{files.length > 0 ? ` (${files.length})` : ""}
        </h4>
        <Button
          variant="ghost"
          size="sm"
          className="-mr-2 h-7 gap-1 text-xs text-muted-foreground"
          onClick={() => setAddOpen(true)}
        >
          <Plus className="h-3.5 w-3.5" /> Add files
        </Button>
      </div>

      {files.length > 0 && (
        <ul className="mt-1.5 flex flex-col gap-1">
          {files.map((file) => (
            <li
              key={file.documentId}
              className="group flex items-center gap-2 rounded-md border border-border px-2.5 py-2 transition-colors hover:bg-muted/50"
            >
              <FileText className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              <button
                type="button"
                className="min-w-0 flex-1 truncate text-left text-sm text-foreground transition-colors hover:text-primary"
                title={file.fileName}
                onClick={() => setViewingId(file.documentId)}
              >
                {file.fileName}
              </button>
              <button
                type="button"
                aria-label={`Remove ${file.fileName}`}
                className="shrink-0 rounded p-0.5 text-muted-foreground opacity-0 transition-opacity hover:text-destructive focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring group-hover:opacity-100"
                onClick={() => void handleDetach(file.documentId)}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}

      <TaskAttachmentsDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        keyDateId={keyDateId}
        jobId={jobId}
      />

      {/* `isViewer={false}`: this panel is an editing surface and carries no
          job-role in scope; document edits are gated server-side regardless. */}
      <DocumentDetail
        documentId={viewingId}
        isViewer={false}
        open={viewingId !== null}
        onClose={() => setViewingId(null)}
      />
    </div>
  );
}
